import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useInvoiceStore, useInvoiceStoreBase, useArchivedInvoiceStore, useArchivedInvoiceStoreBase } from '../stores/invoiceStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, CustomSelect, BulkSelectCell, ListSearchInput, ListPagination } from '@redpanda/ui';
import InvoiceDialog from '../components/InvoiceDialog';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { toast } from 'react-hot-toast';
import { useBulkActions, isAdmin, canDeleteItem, useListSearch, useRememberedState, emitDataChanged } from '@redpanda/utils';
import MultiBulkActionButton from '../components/BulkOperations/MultiBulkActionButton';
import BulkSelectHeader from '../components/BulkOperations/BulkSelectHeader';
import { TableSkeleton } from '@redpanda/ui';
import { bulkDeleteInvoices, archiveSelectedInvoices, restoreArchivedInvoices, getInvoices } from '../services/api';
import { createBulkDeleteOptimisticHandler, createOptimisticBulkDeleteUpdate, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { useInvoiceLimitCache } from '../hooks/useInvoiceLimitCache';
// 發票狀態工具函數（將在組件內部使用 t() 函數，此處移除）

const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    DRAFT: 'text-gray-600',
    SENT: 'text-blue-600',
    PAID: 'text-green-600',
    OVERDUE: 'text-red-600',
    CANCELLED: 'text-gray-500'
  };
  return colorMap[status] || 'text-gray-600';
};

// 年份和月份選項生成將移到組件內部以使用 t() 函數

const formatAmount = (amount: number | string) => {
  return `$${Math.round(Number(amount))}`;
};

type ViewMode = 'active' | 'archived';
const INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'CANCELLED'] as const;

const InvoiceManagePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;

  // 發票狀態標籤函數（使用翻譯）
  const getStatusLabel = (status: string): string => {
    return t(`invoices.status.${status}`, { defaultValue: status });
  };

  // 生成年份選項（使用翻譯）
  const generateYearOptions = (): Array<{ value: string; label: string }> => {
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = 0; i < 6; i++) {
      const year = currentYear - i;
      yearOptions.push({
        value: year.toString(),
        label: `${year}${t('common.time.year')}`
      });
    }
    return yearOptions;
  };

  // 生成月份選項（使用翻譯）
  const generateMonthOptions = (): Array<{ value: string; label: string }> => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthNum = (i + 1).toString();
      return {
        value: monthNum,
        label: t(`common.time.months.${monthNum}`)
      };
    });
  };

  // 確定當前模式，根據路由路徑判斷
  const isArchived = location.pathname === '/invoices/archived';
  const currentMode: ViewMode = isArchived ? 'archived' : 'active';

  // 根據模式選擇對應的 store
  const activeStore = useInvoiceStore();
  const archivedStore = useArchivedInvoiceStore();
  const currentStore = isArchived ? archivedStore : activeStore;

  // 解構當前 store 的屬性
  const {
    invoices,
    pagination,
    error,
    isLoading,
    showDeleteConfirmModal,
    isDeleting,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    refreshData,
  } = currentStore;

  // 發票限制相關 (只在 active 模式下使用)
  const limitCache = useInvoiceLimitCache();
  const { canAddInvoice, setCanAddInvoice, limitBannerRefreshTrigger, setLimitBannerRefreshTrigger } = limitCache;

  // 對話框狀態
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  // 搜尋、篩選由後端查全部發票（docs/介面優化.md R29；原本只過濾目前這一頁，第 2 頁以後搜不到）
  // 狀態篩選記住上次的選擇；搜尋文字、年月（日期）不記住；月份要先選年份
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useRememberedState('accounting-ui:invoices.status', '', ['', ...INVOICE_STATUSES]);
  const changeYear = (year: string) => {
    setSelectedYear(year);
    if (!year) setSelectedMonth('');
  };
  useEffect(() => { // 切換發票／已封存：清掉搜尋與年月
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
  }, [isArchived]);
  const search = searchTerm.trim();
  const hasFilters = !!(search || selectedYear || selectedStatus);
  const filterQuery = {
    archived: isArchived,
    search: search || undefined,
    year: selectedYear || undefined,
    month: (selectedYear && selectedMonth) || undefined,
    status: selectedStatus || undefined,
  };
  const searchResult = useListSearch<any>('invoices', hasFilters ? filterQuery : null, page =>
    getInvoices(page, 20, filterQuery.month, filterQuery.year, isArchived, { search: filterQuery.search, status: filterQuery.status })
  );
  const filteredInvoices = searchResult.active ? searchResult.items : (Array.isArray(invoices) ? invoices : []);
  const displayPagination = searchResult.active ? searchResult.pagination : pagination;

  // 權限檢查
  const canDeleteInvoice = (invoice: any): boolean => {
    // 檢查發票狀態：未付款且未逾期
    const statusAllowsDelete = invoice.status !== 'PAID' && invoice.status !== 'OVERDUE';
    // 檢查使用者權限：SUPER_ADMIN/ADMIN 可刪除所有，USER 只能刪除自己的
    const hasPermission = canDeleteItem(userRole, invoice.userId, currentUserId);
    return statusAllowsDelete && hasPermission;
  };

  const canRestoreInvoice = (invoice: any): boolean => {
    return isAdmin(userRole);
  };

  // 批次操作 hook
  const bulkActions = useBulkActions({
    items: filteredInvoices,
    idField: 'id',
    entityName: t('invoices.item'),
    bulkDeleteApi: bulkDeleteInvoices,
    canDeleteItem: canDeleteInvoice,
    userRole,
    t,
    toast,
    pagination: displayPagination ? {
      currentPage: displayPagination.currentPage,
      totalPages: displayPagination.totalPages,
    } : undefined,
    fetchItems: currentStore.fetchInvoices,
    setCurrentPage: currentStore.setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      (isArchived ? useArchivedInvoiceStoreBase : useInvoiceStoreBase).setState,
      'invoice',
      () => invoices,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await createBulkDeleteSuccessHandler(
        () => (isArchived ? useArchivedInvoiceStoreBase : useInvoiceStoreBase).getState(),
        currentStore.fetchInvoices,
        currentStore.setCurrentPage
      )(deletedIds ?? []);
      emitDataChanged('invoices');
    }
  });
  // 有搜尋、篩選時換頁走後端查詢結果
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  // 清除篩選
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedStatus('');
  };

  // 通用：更新目標 store（封存 → 主列表 或 主列表 → 封存列表）
  const updateTargetStore = (invoiceIds: string[], targetStoreBase: any, archived: boolean) => {
    const transferredInvoices = invoices
      .filter(invoice => invoiceIds.includes(invoice.id))
      .map(invoice => ({ ...invoice, archived }));

    const targetState = targetStoreBase.getState() as any;
    const newTotalItems = targetState.pagination
      ? targetState.pagination.totalItems + transferredInvoices.length
      : transferredInvoices.length;
    const pageSize = targetState.pagination?.pageSize || 20;
    const newTotalPages = Math.max(1, Math.ceil(newTotalItems / pageSize));

    targetStoreBase.setState({
      items: targetState.items && targetState.items.length > 0
        ? [...transferredInvoices, ...targetState.items]
        : transferredInvoices,
      pagination: targetState.pagination ? {
        ...targetState.pagination,
        totalItems: newTotalItems,
        totalPages: newTotalPages
      } : {
        totalItems: newTotalItems,
        totalPages: newTotalPages,
        currentPage: 1,
        pageSize: 20
      },
      lastOptimisticUpdate: Date.now(),
    });
  };

  // 處理封存
  const handleBulkArchive = async () => {
    const selectedIds = bulkActions.selectedItems;
    if (selectedIds.length === 0) {
      toast.error(t('invoices.list.selectInvoicesForArchive'));
      return;
    }

    // 檢查選擇的發票是否都可以封存
    const selectedInvoices = filteredInvoices.filter(invoice => selectedIds.includes(invoice.id));
    const cannotArchiveInvoices = selectedInvoices.filter(invoice =>
      invoice.status === 'CANCELLED' // 已取消的發票不能封存
    );

    if (cannotArchiveInvoices.length > 0) {
      toast.error(t('invoices.list.archiveStatusError', { count: cannotArchiveInvoices.length }));
      return;
    }

    try {
      // 先執行樂觀更新
      const optimisticUpdate = createOptimisticBulkDeleteUpdate(useInvoiceStoreBase.setState, 'invoice');
      optimisticUpdate(selectedIds, invoices, pagination);

      // 清除選擇
      bulkActions.setSelectedItems([]);

      const response = await archiveSelectedInvoices(selectedIds);

      if (response.data.archivedCount > 0) {
        toast.success(t('invoices.list.archiveSuccess', { count: response.data.archivedCount }));
        emitDataChanged('invoices'); // 搜尋結果重抓

        // 觸發限制橫幅重新載入
        setLimitBannerRefreshTrigger(prev => prev + 1);

        // 更新封存發票列表（使用樂觀更新，不清除快取）
        updateTargetStore(selectedIds, useArchivedInvoiceStoreBase, true);

        // 同步更新所有相關列表中的發票狀態
        import('../utils/invoiceStatusUpdater').then(({ archiveInvoices }) => {
          archiveInvoices(selectedIds);
        });

        const handler = createBulkDeleteSuccessHandler(
          () => useInvoiceStoreBase.getState(),
          currentStore.fetchInvoices,
          currentStore.setCurrentPage
        );
        await handler(selectedIds);
      }

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((error: string) => {
          toast.error(error);
        });
        await refreshData();
      }
    } catch (error: any) {
      console.error('封存發票失敗:', error);
      const errorMessage = error.response?.data?.error || t('invoices.list.archiveError');
      toast.error(errorMessage);
      await refreshData();
    }
  };

  // 單一發票恢復
  const handleRestoreSingleInvoice = async (invoiceId: string) => {
    try {
      // 先執行樂觀更新
      const optimisticUpdate = createOptimisticBulkDeleteUpdate(useArchivedInvoiceStoreBase.setState, 'archivedInvoice');
      optimisticUpdate([invoiceId], invoices, pagination);

      const response = await restoreArchivedInvoices([invoiceId]);

      if (response.data.restoredCount > 0) {
        toast.success(t('invoices.list.restoreSuccess'));
        emitDataChanged('invoices');

        // 更新主發票列表（使用樂觀更新，不清除快取）
        updateTargetStore([invoiceId], useInvoiceStoreBase, false);

        // 觸發限制橫幅重新載入
        setLimitBannerRefreshTrigger(prev => prev + 1);

        // 同步更新所有相關列表中的發票狀態
        import('../utils/invoiceStatusUpdater').then(({ restoreInvoices }) => {
          restoreInvoices([invoiceId]);
        });
      }

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((error: string) => {
          toast.error(error);
        });
        await refreshData();
      }
    } catch (error: any) {
      console.error('恢復發票失敗:', error);
      const errorMessage = error.response?.data?.error || t('invoices.list.restoreError');
      toast.error(errorMessage);
      await refreshData();
    }
  };

  // 批量恢復
  const handleRestoreInvoices = async () => {
    const selectedIds = bulkActions.selectedItems;
    if (selectedIds.length === 0) {
      toast.error(t('invoices.list.selectInvoicesToRestore'));
      return;
    }

    try {
      // 先執行樂觀更新
      const optimisticUpdate = createOptimisticBulkDeleteUpdate(useArchivedInvoiceStoreBase.setState, 'archivedInvoice');
      optimisticUpdate(selectedIds, invoices, pagination);

      // 清除選擇
      bulkActions.setSelectedItems([]);

      const response = await restoreArchivedInvoices(selectedIds);

      if (response.data.restoredCount > 0) {
        toast.success(t('invoices.list.restoreMultipleSuccess', { count: response.data.restoredCount }));
        emitDataChanged('invoices');

        // 更新主發票列表（使用樂觀更新，不清除快取）
        updateTargetStore(selectedIds, useInvoiceStoreBase, false);

        // 觸發限制橫幅重新載入
        setLimitBannerRefreshTrigger(prev => prev + 1);

        // 同步更新所有相關列表中的發票狀態
        import('../utils/invoiceStatusUpdater').then(({ restoreInvoices }) => {
          restoreInvoices(selectedIds);
        });

        const handler = createBulkDeleteSuccessHandler(
          () => useArchivedInvoiceStoreBase.getState(),
          currentStore.fetchInvoices,
          currentStore.setCurrentPage
        );
        await handler(selectedIds);
      }

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((error: string) => {
          toast.error(error);
        });
        await refreshData();
      }
    } catch (error: any) {
      console.error('恢復發票失敗:', error);
      const errorMessage = error.response?.data?.error || t('invoices.list.restoreError');
      toast.error(errorMessage);
      await refreshData();
    }
  };

  // 對話框處理
  const handleAddInvoice = () => {
    setSelectedInvoice(null);
    setIsDialogOpen(true);
  };

  const handleEditInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setIsDialogOpen(true);
  };

  const handleCloseInvoiceDialog = () => {
    setIsDialogOpen(false);
  };

  // 模式切換
  // 載入邏輯
  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      if (currentMode === 'archived') {
        archivedStore.fetchInvoices(1);
      } else {
        activeStore.fetchInvoices(1);
      }
    }
  }, [isAuthenticated, isUserLoading, currentMode]); // 只依賴最基本的狀態

  // 切換模式時清除批量選取
  useEffect(() => {
    if (bulkActions.hasSelectedItems) {
      bulkActions.setSelectedItems([]);
    }
  }, [currentMode]); // 只依賴 currentMode，避免循環

  // 監聽發票總數變化，用於單個刪除時更新 InvoiceLimitBanner（只在非封存模式）
  const prevInvoiceCountRef = useRef(filteredInvoices.length);
  useEffect(() => {
    if (!isArchived) {
      const currentCount = filteredInvoices.length;
      const prevCount = prevInvoiceCountRef.current;

      // 如果發票數量減少（表示有刪除操作），觸發 InvoiceLimitBanner 更新
      if (currentCount < prevCount && prevCount > 0) {
        setLimitBannerRefreshTrigger(prev => prev + 1);
      }

      prevInvoiceCountRef.current = currentCount;
    }
  }, [filteredInvoices.length, setLimitBannerRefreshTrigger, isArchived]);

  // ✅ 修正：載入時顯示骨架而非空白，避免完全沒有畫面
  if (!isAuthenticated && !isUserLoading) {
    return (
      <div className="p-8 text-center text-red-500">
        {t('invoices.list.unauthorized')}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題和模式切換 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {isUserLoading ? (
              <div className="h-9 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-48"></div>
            ) : (
              isArchived ? t('invoices.list.archivedTitle') : t('invoices.list.title')
            )}
          </h1>

          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                onArchive={isArchived ? undefined : handleBulkArchive}
                onRestore={isArchived ? handleRestoreInvoices : undefined}
                showArchive={!isArchived}
                showRestore={isArchived}
              />
            )}

            {/* 新增按鈕 - 只在 active 模式顯示 */}
            {!isArchived && (
              <button
                onClick={handleAddInvoice}
                disabled={!canAddInvoice}
                className={`font-bold py-2 px-4 rounded ${
                  canAddInvoice
                    ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={canAddInvoice ? undefined : t('invoices.list.limitExceeded')}
              >
                {t('invoices.list.addButton')}
              </button>
            )}

            {/* 查看封存按鈕或返回列表按鈕 */}
            {!isArchived ? (
              <Link to="/invoices/archived">
                <Button variant="secondary">
                  {t('invoices.list.viewArchived')}
                </Button>
              </Link>
            ) : (
              <Link to="/invoices">
                <Button variant="secondary">
                  {t('invoices.list.backToList')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* 發票限制橫幅 */}
      {!isArchived ? (
        <InvoiceLimitBanner onLimitChange={setCanAddInvoice} refreshTrigger={limitBannerRefreshTrigger} />
      ) : (
        <InvoiceLimitBanner refreshTrigger={limitBannerRefreshTrigger} />
      )}

      {/* 搜尋和篩選區域 */}
      <div className="mb-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* 搜尋框 */}
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('invoices.list.searchPlaceholder')} widthClass="" />

          {/* 年份篩選 */}
          <div>
            <CustomSelect
              value={selectedYear}
              onChange={changeYear}
              placeholder={t('invoices.list.selectYear')}
              className="h-10"
              options={generateYearOptions()}
            />
          </div>

          {/* 月份篩選 */}
          <div>
            <CustomSelect
              value={selectedMonth}
              onChange={setSelectedMonth}
              disabled={!selectedYear}
              placeholder={t('invoices.list.selectMonth')}
              className="h-10"
              options={generateMonthOptions()}
            />
          </div>

          {/* 狀態篩選 */}
          <div>
            <CustomSelect
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder={t('invoices.list.selectStatus')}
              className="h-10"
              options={INVOICE_STATUSES.map(status => ({ value: status, label: t(`invoices.status.${status}`) }))}
            />
          </div>

          {/* 清除篩選按鈕 */}
          <div className="flex flex-col space-y-2">
            <Button
              type="button"
              onClick={clearFilters}
              variant="ghost"
              className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-md h-10 flex items-center justify-center"
            >
              {t('invoices.list.clearFilters')}
            </Button>
          </div>
        </div>
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} className="mb-3" />

      {/* 表格區域 */}
      {error ? (
        <div className="p-8 text-center text-red-500">
          {t('invoices.list.loadError')}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {isLoading && invoices.length === 0 ? (
              <TableSkeleton />
            ) : (
              <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
                <TableHeader>
                  <TableRow>
                    <BulkSelectHeader
                      userRole={userRole}
                      allSelected={bulkActions.allSelected}
                      onToggleAll={bulkActions.toggleSelectAll}
                      hasItems={filteredInvoices.length > 0}
                    />
                    <TableHead className="w-32">{t('invoices.table.number')}</TableHead>
                    <TableHead className="w-64">{t('invoices.table.customerName')}</TableHead>
                    <TableHead className="w-28">{t('invoices.table.date')}</TableHead>
                    <TableHead className="w-28">{t('invoices.table.dueDate')}</TableHead>
                    <TableHead className="w-24">{t('invoices.table.amount')}</TableHead>
                    <TableHead className="w-20">{t('invoices.table.status')}</TableHead>
                    <TableHead className="w-24">{t('invoices.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      {(searchTerm || selectedYear || selectedMonth || selectedStatus)
                        ? t('invoices.list.noMatchingInvoices')
                        : (isArchived ? t('invoices.list.noArchivedInvoices') : t('invoices.list.noInvoices'))
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={bulkActions.isItemSelected(invoice.id)}
                        onToggle={() => bulkActions.toggleItemSelection(invoice.id)}
                        canDelete={canDeleteInvoice(invoice) && (isArchived || invoice.status !== 'CANCELLED')}
                        disabledReason={invoice.status === 'CANCELLED' && !isArchived ? t('invoices.list.cannotArchiveCancelled') : undefined}
                      />
                      <TableCell className="w-32 font-medium truncate">{invoice.number}</TableCell>
                      <TableCell className="w-64 truncate">{invoice.customerName}</TableCell>
                      <TableCell className="w-28 truncate">{invoice.date ? new Date(invoice.date).toLocaleDateString('zh-TW') : '-'}</TableCell>
                      <TableCell className="w-28 truncate">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('zh-TW') : '-'}</TableCell>
                      <TableCell className="w-24 truncate">{formatAmount(invoice.totalAmount)}</TableCell>
                      <TableCell>
                        <span className={`font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusLabel(invoice.status)}
                        </span>
                      </TableCell>
                      <TableCell className="w-24">
                        {isArchived ? (
                          <button
                            onClick={() => handleRestoreSingleInvoice(invoice.id)}
                            disabled={!canRestoreInvoice(invoice)}
                            className={`text-sm ${
                              canRestoreInvoice(invoice)
                                ? 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-200'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {t('invoices.table.restore')}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditInvoice(invoice)}
                              className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 mr-3"
                            >
                              {t('common.actions.edit')}
                            </button>
                            <button
                              onClick={() => handleDelete(invoice.id)}
                              disabled={!canDeleteInvoice(invoice)}
                              title={!canDeleteInvoice(invoice) ? t('invoices.list.cannotDeletePaidOrOverdue') : ''}
                              className={`text-sm ${
                                canDeleteInvoice(invoice)
                                  ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              {t('common.actions.delete')}
                            </button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            )}
          </div>
        </div>
      )}

      {/* 分頁 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} className="mt-6" />

      {/* 對話框 */}
      {isDialogOpen && (
        <InvoiceDialog
          invoice={selectedInvoice}
          isOpen={isDialogOpen}
          onClose={handleCloseInvoiceDialog}
          onInvoiceCreated={(savedInvoice?: any, isEdit?: boolean) => {
            // 樂觀更新已在 InvoiceDialog 中完成，這裡只處理副作用
            if (savedInvoice && !isArchived) {
              if (!isEdit) {
                // 新增發票後觸發限制刷新
                setLimitBannerRefreshTrigger(prev => prev + 1);
              }
              // 清除對應年份的報表快取
              const invoiceYear = new Date(savedInvoice.date || savedInvoice.issueDate).getFullYear().toString();
              import('../stores/reportStore').then(({ useReportStore }) => {
                const reportStore = useReportStore.getState();
                reportStore.clearYearCache(invoiceYear);
              });
            }
            // 背景刷新確保資料一致性（搜尋結果也重抓）
            currentStore.fetchInvoices(pagination?.currentPage || 1, false);
            emitDataChanged('invoices');
          }}
        />
      )}

      {/* 刪除確認對話框 */}
      {showDeleteConfirmModal && (
        <ConfirmModal
          isOpen={showDeleteConfirmModal}
          onClose={closeDeleteModal}
          onConfirm={async () => {
            await confirmDelete();
            emitDataChanged('invoices');
          }}
          title={t('invoices.list.confirmDeleteTitle')}
          message={t('invoices.list.confirmDeleteMessage')}
          confirmText={t('common.bulk.deleteAction')}
          cancelText={t('common.actions.cancel')}
          isLoading={isDeleting}
        />
      )}

      {/* 批量刪除確認對話框 */}
      {bulkActions.showBulkDeleteModal && (
        <ConfirmModal
          isOpen={bulkActions.showBulkDeleteModal}
          onClose={bulkActions.closeBulkDeleteModal}
          onConfirm={bulkActions.confirmBulkDelete}
          title={t('invoices.list.confirmBulkDeleteTitle')}
          message={t('invoices.list.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
          confirmText={t('common.bulk.deleteAction')}
          cancelText={t('common.actions.cancel')}
          isLoading={bulkActions.isBulkDeleting}
        />
      )}
    </div>
  );
};

export default InvoiceManagePage;